import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowLeft, TrendingUp, Plus, Trash2, AlertTriangle } from 'lucide-react';
import { api } from '@/lib/api';
import { useMembership } from '@/hooks/useMembership';
import { useDebouncedSignal } from '@/hooks/useDebouncedSignal';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import {
  NumericInput,
  SelectInput,
  TextArea,
  AutoSaveIndicator,
  type SaveStatus,
} from '@/components/entry';
import { HarvestDialog, type HarvestValues } from '@/components/entry/HarvestDialog';
import { cn } from '@/lib/cn';
import { format, parse } from 'date-fns';
import { sameFieldValue, toNumber, toNumberOrNull } from '@/lib/number';
import {
  createFailureMessage,
  isUnconfirmedWrite,
  registerAutosaveFlusher,
  saveFailureMessage,
} from '@/lib/apiErrors';
import { writeSignal } from '@/lib/writeSignal';
import { useUnsavedEditsWarning } from '@/hooks/useUnsavedEditsWarning';
import { todayInPH } from '@/lib/phDate';
import { qk } from '@/lib/queryKeys';
import { SIZE_GRADE_OPTIONS } from '@/lib/sizeGrades';
import type { Harvest } from '@/types/entry';
import type { ApiResponse } from '@/types/api';
import type { PondDetailResponse } from '@/types/generated/openapi';
import { randomId } from '@/lib/randomId';

interface HarvestSearchParams {
  date?: string;
}

export const Route = createFileRoute('/_app/entry/pond/$pondId/harvest')({
  validateSearch: (search: Record<string, unknown>): HarvestSearchParams => {
    return {
      date: typeof search.date === 'string' ? search.date : undefined,
    };
  },
  component: HarvestForm,
});

function HarvestForm() {
  const { pondId } = Route.useParams();
  const { date } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { membership, isLoading: membershipLoading, isSupervisorOrAbove } = useMembership();
  // Technicians record harvests; corrections, deletes and final harvests are supervisor+ (#156).
  const canEdit = isSupervisorOrAbove;

  const dateStr = date || todayInPH();
  const displayDate = parse(dateStr, 'yyyy-MM-dd', new Date());

  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [draftById, setDraftById] = useState<Record<string, Partial<Harvest>>>({});
  const pendingChangesRef = useRef<Record<string, Partial<Harvest>>>({});
  const typedRef = useRef<
    Record<string, { total_weight_kg?: number | null; piece_count?: number | null; price_per_kg?: number | null; notes?: string }>
  >({});
  const [hasPending, setHasPending] = useState(false);
  const [dialogType, setDialogType] = useState<'partial' | 'final' | null>(null);

  // Fetch pond data
  const { data: pond, isLoading: pondLoading } = useQuery({
    queryKey: qk.pond.detail(membership?.farmId, pondId),
    queryFn: async () => {
      const response = await api.get<ApiResponse<PondDetailResponse>>(
        `/api/v1/farms/${membership?.farmId}/ponds/${pondId}/detail`
      );
      return response.data;
    },
    enabled: !!pondId && !!membership?.farmId,
  });

  const cycleId = pond?.active_cycle?.id;
  const harvestsKey = ['harvests', cycleId, dateStr] as const;

  const resetKey = `${cycleId}-${dateStr}`;
  const [prevResetKey, setPrevResetKey] = useState(resetKey);
  if (prevResetKey !== resetKey) {
    setPrevResetKey(resetKey);
    setDraftById({});
    setHasPending(false);
  }
  useEffect(() => {
    pendingChangesRef.current = {};
    typedRef.current = {};
  }, [resetKey]);

  const writeScope = { id: `harvest-writes-${cycleId}` };

  // Fetch harvests for the day
  const { data: harvests, isLoading: harvestsLoading } = useQuery({
    queryKey: harvestsKey,
    queryFn: async () => {
      const response = await api.get<{ data: Harvest[] }>(
        `/api/v1/farms/${membership?.farmId}/harvests`,
        {
          params: {
            cycle_id: cycleId!,
            start_date: dateStr,
            end_date: dateStr,
          },
        }
      );
      return response.data || [];
    },
    enabled: !!cycleId && !!membership?.farmId,
  });

  // Calculate totals
  const totals = useMemo(() => {
    if (!harvests?.length) return { weight: 0, count: 0, value: 0 };

    return harvests.reduce(
      (acc, h) => {
        const draft = draftById[h.id];
        const weight = toNumber(draft?.total_weight_kg ?? h.total_weight_kg, 0);
        const count = toNumber(draft?.piece_count ?? h.piece_count, 0);
        const price = toNumber(draft?.price_per_kg ?? h.price_per_kg, 0);

        return {
          weight: acc.weight + weight,
          count: acc.count + count,
          value: acc.value + weight * price,
        };
      },
      { weight: 0, count: 0, value: 0 }
    );
  }, [draftById, harvests]);

  const hasFinalHarvest = harvests?.some((h) => h.harvest_type === 'final');

  // Create mutation
  const createMutation = useMutation({
    scope: writeScope,
    // POST isn't idempotent: a retry after a lost response would record the harvest twice.
    retry: false,
    mutationFn: async (data: Partial<Harvest>) => {
      return api.post(
        `/api/v1/farms/${membership?.farmId}/harvests`,
        {
          cycle_id: cycleId,
          harvest_date: dateStr,
          ...data,
        },
        { signal: writeSignal() }
      );
    },
    onMutate: async (data) => {
      if (!cycleId) return undefined;
      await queryClient.cancelQueries({ queryKey: harvestsKey });
      const previous = queryClient.getQueryData<Harvest[]>(harvestsKey);
      const nowIso = new Date().toISOString();
      const optimistic: Harvest = {
        id: `optimistic-${randomId()}`,
        farm_id: membership?.farmId,
        cycle_id: cycleId,
        harvest_date: dateStr,
        total_weight_kg: data.total_weight_kg ?? 0,
        piece_count: data.piece_count ?? null,
        avg_weight_g: data.avg_weight_g ?? null,
        buyer: data.buyer ?? null,
        size_grade: data.size_grade ?? null,
        price_per_kg: data.price_per_kg ?? null,
        harvest_type: data.harvest_type ?? 'partial',
        notes: data.notes ?? null,
        total_revenue: null,
        created_at: nowIso,
        last_modified: nowIso,
        deleted_at: null,
      };
      const next = previous ? [...previous, optimistic] : [optimistic];
      queryClient.setQueryData(harvestsKey, next);
      return { previous, optimisticId: optimistic.id };
    },
    onError: (err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(harvestsKey, context.previous);
      }
      // A timed-out create may have committed: let the list show it, and don't leave the
      // dialog open for a second click to record it again (a duplicate final harvest).
      if (isUnconfirmedWrite(err)) {
        queryClient.invalidateQueries({ queryKey: harvestsKey });
        setDialogType(null);
      }
      toast.error(createFailureMessage(err, 'Could not record harvest'), { id: 'harvest-create-error' });
      if (context?.optimisticId) {
        delete pendingChangesRef.current[context.optimisticId];
        delete typedRef.current[context.optimisticId];
      }
    },
    onSuccess: (result, _vars, context) => {
      const created = (result as { data?: Harvest })?.data ?? (result as Harvest | undefined);
      const current = queryClient.getQueryData<Harvest[]>(harvestsKey) || [];
      let next = current;
      if (context?.optimisticId) {
        next = current.filter((h) => h.id !== context.optimisticId);
      }
      if (created) {
        next = [...next, created];
      }
      queryClient.setQueryData(harvestsKey, next);
      if (context?.optimisticId) delete typedRef.current[context.optimisticId];
      if (created && context?.optimisticId) {
        setDraftById((prev) => {
          if (!(context.optimisticId in prev)) return prev;
          const { [context.optimisticId]: draft, ...rest } = prev;
          return { ...rest, [created.id]: draft };
        });
        if (pendingChangesRef.current[context.optimisticId]) {
          const pending = pendingChangesRef.current[context.optimisticId];
          const nextPending = { ...pendingChangesRef.current };
          delete nextPending[context.optimisticId];
          nextPending[created.id] = pending;
          pendingChangesRef.current = nextPending;
          setHasPending(true);
          setSaveStatus('saving');
          flushNow();
        }
      }
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    scope: writeScope,
    mutationFn: async ({ id, ...data }: { id: string } & Partial<Harvest>) => {
      return api.put(`/api/v1/farms/${membership?.farmId}/harvests/${id}`, data, {
        signal: writeSignal(),
      });
    },
    onMutate: async ({ id, ...data }) => {
      setSaveStatus('saving');
      if (!cycleId) return undefined;
      await queryClient.cancelQueries({ queryKey: harvestsKey });
      const previous = queryClient.getQueryData<Harvest[]>(harvestsKey);
      const previousRow = previous?.find((h) => h.id === id);
      if (previous) {
        const next = previous.map((h) => (h.id === id ? { ...h, ...data } : h));
        queryClient.setQueryData(harvestsKey, next);
      }
      return { previousRow };
    },
    onError: (err, vars, context) => {
      setSaveStatus('error');
      // A save sent on leave has no indicator left to show the failure.
      toast.error(saveFailureMessage(err, 'Unable to save harvest'), { id: 'harvest-save-error' });
      // Only revert the failed row, not the entire list
      if (context?.previousRow) {
        queryClient.setQueryData(harvestsKey, (old: Harvest[] | undefined) => {
          if (!old) return old;
          return old.map((item) =>
            item.id === vars.id ? context.previousRow! : item
          );
        });
      }
    },
    onSuccess: (result) => {
      setSaveStatus('saved');
      const updated = (result as { data?: Harvest })?.data ?? (result as Harvest | undefined);
      if (updated) {
        const current = queryClient.getQueryData<Harvest[]>(harvestsKey) || [];
        const next = current.map((h) => (h.id === updated.id ? updated : h));
        queryClient.setQueryData(harvestsKey, next);
      }
      setTimeout(() => setSaveStatus('idle'), 2000);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    scope: writeScope,
    mutationFn: async (id: string) => {
      return api.delete(`/api/v1/farms/${membership?.farmId}/harvests/${id}`, {
        signal: writeSignal(),
      });
    },
    onMutate: async (id) => {
      if (!cycleId) return undefined;
      await queryClient.cancelQueries({ queryKey: harvestsKey });
      const previous = queryClient.getQueryData<Harvest[]>(harvestsKey);
      if (previous) {
        queryClient.setQueryData(harvestsKey, previous.filter((h) => h.id !== id));
      }
      setDraftById((prev) => {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
      delete typedRef.current[id];
      if (pendingChangesRef.current[id]) {
        const next = { ...pendingChangesRef.current };
        delete next[id];
        pendingChangesRef.current = next;
        setHasPending(Object.keys(next).length > 0);
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(harvestsKey, context.previous);
      }
    },
    onSuccess: () => {
      // No refetch needed; optimistic update already applied.
    },
  });

  const flushPending = useCallback(() => {
    const pending = pendingChangesRef.current;
    const ids = Object.keys(pending);
    if (!ids.length) return;

    const remaining: Record<string, Partial<Harvest>> = {};
    ids.forEach((id) => {
      if (id.startsWith('optimistic-')) {
        remaining[id] = pending[id];
        return;
      }
      updateMutation.mutate({ id, ...pending[id] });
    });

    pendingChangesRef.current = remaining;
    setHasPending(Object.keys(remaining).length > 0);
  }, [updateMutation]);

  const { signal: scheduleSave, flush: flushNow } = useDebouncedSignal(flushPending, {
    delayMs: 600,
    flushOnHide: true,
  });

  // No deps: flushAllPendingAutosaves empties the registry, so register again on every render.
  useEffect(() => registerAutosaveFlusher(flushNow));

  const queueFieldSave = useCallback(
    (harvest: Harvest, field: keyof Harvest, value: unknown) => {
      const harvestId = harvest.id;
      const currentValue = harvest[field];

      setDraftById((prev) => ({
        ...prev,
        [harvestId]: {
          ...prev[harvestId],
          [field]: value as Harvest[keyof Harvest],
        },
      }));

      if (sameFieldValue(value, currentValue)) {
        const existing = pendingChangesRef.current[harvestId];
        if (existing && field in existing) {
          const next = { ...pendingChangesRef.current };
          const nextFields = { ...existing };
          delete nextFields[field];
          if (Object.keys(nextFields).length) {
            next[harvestId] = nextFields;
          } else {
            delete next[harvestId];
          }
          pendingChangesRef.current = next;
          setHasPending(Object.keys(next).length > 0);
        }
        return;
      }

      pendingChangesRef.current = {
        ...pendingChangesRef.current,
        [harvestId]: {
          ...pendingChangesRef.current[harvestId],
          [field]: value as Harvest[keyof Harvest],
        },
      };
      setHasPending(true);
      setSaveStatus('saving');
      scheduleSave();
    },
    [scheduleSave]
  );

  // The debounce is cancelled on unmount, and a focused field never blurs when its route is
  // torn down (browser back with the keyboard open), so an edit made inside the 600 ms
  // window would be dropped. The cleanup runs before the reset effect wipes the buffer.
  const leaveRef = useRef<() => void>(() => {});
  useEffect(() => {
    leaveRef.current = () => {
      harvests?.forEach((harvest) => {
        const typed = typedRef.current[harvest.id];
        if (!typed) return;
        const draft = draftById[harvest.id];
        if (typed.total_weight_kg !== undefined) {
          queueFieldSave(
            harvest,
            'total_weight_kg',
            typed.total_weight_kg ?? draft?.total_weight_kg ?? harvest.total_weight_kg ?? 0
          );
        }
        if (typed.piece_count !== undefined) queueFieldSave(harvest, 'piece_count', typed.piece_count);
        if (typed.price_per_kg !== undefined) queueFieldSave(harvest, 'price_per_kg', typed.price_per_kg);
        if (typed.notes !== undefined) queueFieldSave(harvest, 'notes', typed.notes);
      });
      typedRef.current = {};
      flushNow();
    };
  });
  useEffect(() => () => leaveRef.current(), [resetKey]);

  useUnsavedEditsWarning(
    () =>
      Object.values(typedRef.current).some((t) => Object.values(t).some((v) => v !== undefined)) ||
      Object.keys(pendingChangesRef.current).length > 0 ||
      queryClient.isMutating() > 0
  );

  const commitField = (
    harvest: Harvest,
    field: 'total_weight_kg' | 'piece_count' | 'price_per_kg' | 'notes',
    value: unknown
  ) => {
    const typed = typedRef.current[harvest.id];
    if (typed) delete typed[field];
    queueFieldSave(harvest, field, value);
    flushNow();
  };

  const handleConfirmHarvest = (harvestType: 'partial' | 'final', values: HarvestValues) => {
    createMutation.mutate(
      { harvest_type: harvestType, ...values },
      { onSuccess: () => setDialogType(null) }
    );
  };

  if (membershipLoading || pondLoading || harvestsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate({ to: `/entry/pond/${pondId}`, search: { date: dateStr } })}
            className="p-2 -ml-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-rose-50">
                <TrendingUp className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{pond?.name}</h1>
                <p className="text-sm text-gray-500">
                  Harvests • {format(displayDate, 'EEEE, MMMM d')}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={flushNow}
            disabled={!hasPending || updateMutation.isPending}
          >
            Save All
          </Button>
          <AutoSaveIndicator
            status={saveStatus}
            onRetry={() => updateMutation.reset()}
          />
        </div>
      </div>

      {/* Totals Summary */}
      {harvests && harvests.length > 0 && (
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-200 p-5">
          <h3 className="font-semibold text-emerald-900 mb-4">Harvest Summary</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white/60 rounded-lg p-4 border border-emerald-100">
              <p className="text-xs text-emerald-600 mb-1">Total Weight</p>
              <p className="text-2xl font-bold text-emerald-900 tabular-nums">
                {totals.weight.toFixed(2)} <span className="text-sm font-normal">kg</span>
              </p>
            </div>
            <div className="bg-white/60 rounded-lg p-4 border border-emerald-100">
              <p className="text-xs text-emerald-600 mb-1">Total Pieces</p>
              <p className="text-2xl font-bold text-emerald-900 tabular-nums">
                {totals.count.toLocaleString()}
              </p>
            </div>
            <div className="bg-white/60 rounded-lg p-4 border border-emerald-100">
              <p className="text-xs text-emerald-600 mb-1">Est. Value</p>
              <p className="text-2xl font-bold text-emerald-900 tabular-nums">
                ₱{totals.value.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Final Harvest Warning */}
      {hasFinalHarvest && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-900">Final harvest recorded</p>
            <p className="text-sm text-amber-700 mt-1">
              This cycle will be marked as complete. All remaining shrimp should be harvested.
            </p>
          </div>
        </div>
      )}

      {/* Harvest Records */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">
            Harvest Records ({harvests?.length || 0})
          </h2>
          <div className="flex gap-2">
            <Button
              onClick={() => setDialogType('partial')}
              disabled={createMutation.isPending || hasFinalHarvest}
              variant="outline"
              size="sm"
            >
              <Plus className="w-4 h-4 mr-1" />
              Partial
            </Button>
            {canEdit && (
              <Button
                onClick={() => setDialogType('final')}
                disabled={createMutation.isPending || hasFinalHarvest}
                variant="primary"
                size="sm"
              >
                <Plus className="w-4 h-4 mr-1" />
                Final
              </Button>
            )}
          </div>
        </div>

        {!canEdit && (
          <p className="text-sm text-gray-500">A Farm Technician records the final harvest.</p>
        )}

        {harvests?.length === 0 ? (
          <div className="bg-white rounded-xl border border-dashed border-gray-300 p-8 text-center">
            <TrendingUp className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 mb-4">No harvests recorded for today</p>
            <div className="flex justify-center gap-3">
              <Button
                onClick={() => setDialogType('partial')}
                variant="outline"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Partial Harvest
              </Button>
              {canEdit && (
                <Button onClick={() => setDialogType('final')}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Final Harvest
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {harvests?.map((harvest) => {
              const draft = draftById[harvest.id];
              const weightValue = draft?.total_weight_kg ?? harvest.total_weight_kg;
              const pieceCountValue = draft?.piece_count ?? harvest.piece_count;
              const sizeGradeValue = draft?.size_grade ?? harvest.size_grade;
              const priceValue = draft?.price_per_kg ?? harvest.price_per_kg;
              const notesValue = draft?.notes ?? harvest.notes;

              return (
                <div
                  key={harvest.id}
                  className={cn(
                    'bg-white rounded-xl border shadow-sm overflow-hidden',
                    harvest.harvest_type === 'final'
                      ? 'border-rose-200 bg-gradient-to-br from-white to-rose-50/30'
                      : 'border-gray-200'
                  )}
                >
                <div className="px-5 py-3 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between">
                  <Badge
                    variant={harvest.harvest_type === 'final' ? 'critical' : 'neutral'}
                    size="sm"
                  >
                    {harvest.harvest_type === 'final' ? 'Final Harvest' : 'Partial Harvest'}
                  </Badge>
                  {canEdit && !harvest.id.startsWith('optimistic-') && (
                    <button
                      // Keep focus in the field: a blur here would PUT a row about to be deleted.
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => deleteMutation.mutate(harvest.id)}
                      className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                      title="Remove"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className="p-5">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <NumericInput
                      disabled={!canEdit}
                      label="Weight"
                      unit="kg"
                      value={toNumberOrNull(weightValue)}
                      min={0}
                      step={0.1}
                      onValueChange={(value) => {
                        typedRef.current[harvest.id] = { ...typedRef.current[harvest.id], total_weight_kg: value };
                      }}
                      onBlur={(value) => commitField(harvest, 'total_weight_kg', value ?? weightValue ?? 0)}
                    />
                    <NumericInput
                      disabled={!canEdit}
                      label="Piece Count"
                      integer
                      value={toNumberOrNull(pieceCountValue)}
                      min={0}
                      step={1}
                      onValueChange={(value) => {
                        typedRef.current[harvest.id] = { ...typedRef.current[harvest.id], piece_count: value };
                      }}
                      onBlur={(value) => commitField(harvest, 'piece_count', value)}
                    />
                    <SelectInput
                      disabled={!canEdit}
                      label="Size Grade"
                      value={sizeGradeValue || ''}
                      options={SIZE_GRADE_OPTIONS}
                      onChange={(value) => queueFieldSave(harvest, 'size_grade', value)}
                    />
                    <NumericInput
                      disabled={!canEdit}
                      label="Price per kg"
                      unit="₱"
                      value={toNumberOrNull(priceValue)}
                      min={0}
                      step={1}
                      onValueChange={(value) => {
                        typedRef.current[harvest.id] = { ...typedRef.current[harvest.id], price_per_kg: value };
                      }}
                      onBlur={(value) => commitField(harvest, 'price_per_kg', value)}
                    />
                  </div>

                  {/* Calculated value */}
                  {toNumber(weightValue, 0) > 0 && toNumber(priceValue, 0) > 0 && (
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">
                        Estimated value:{' '}
                        <span className="font-semibold text-gray-900">
                          ₱{(toNumber(weightValue, 0) * toNumber(priceValue, 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </span>
                      </p>
                    </div>
                  )}

                  <div className="mt-4">
                    <TextArea
                      disabled={!canEdit}
                      label="Notes"
                      value={notesValue || ''}
                      onValueChange={(value) => {
                        typedRef.current[harvest.id] = { ...typedRef.current[harvest.id], notes: value };
                      }}
                      onBlur={(value) => commitField(harvest, 'notes', value)}
                      rows={2}
                      placeholder="Buyer, market conditions, observations..."
                    />
                  </div>
                </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {dialogType && (
        <HarvestDialog
          open
          harvestType={dialogType}
          pondName={pond?.name}
          submitting={createMutation.isPending}
          onClose={() => setDialogType(null)}
          onConfirm={(values) => handleConfirmHarvest(dialogType, values)}
        />
      )}

      {/* Navigation */}
      <div className="flex justify-between items-center pt-4 border-t border-gray-200">
        <Link
          to="/entry/pond/$pondId"
          params={{ pondId }}
          search={{ date: dateStr }}
          className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
        >
          ← Back to entry hub
        </Link>
      </div>
    </div>
  );
}
